// A form document (discharge summary, inspection sheet) arrives as a whole page
// with its own <style> in the <head>. Dropping that markup straight into the
// app makes those rules global, and the documents style generic names —
// `.header`, `.container`, `.section`, `.footer`, plus bare `table`/`th`/`h1` —
// which the app uses for its own chrome. The result is the surrounding UI being
// silently re-laid-out by whatever document happens to be on screen.
//
// So every selector is rewritten to sit under the element the document is
// rendered into. The browser's own CSS parser does the reading, which keeps
// @media blocks, shorthands and escapes intact.

const ROOT_SELECTOR = /^(html|body|:root)\b/i;

const scopeSelectorText = (selectorText, scope) =>
  selectorText
    .split(",")
    .map((part) => {
      const selector = part.trim();
      if (!selector) return selector;
      // `body { … }` is the document's own root, which is now the container.
      if (ROOT_SELECTOR.test(selector))
        return selector.replace(ROOT_SELECTOR, scope);
      return `${scope} ${selector}`;
    })
    .join(", ");

const scopeRule = (rule, scope) => {
  if (typeof CSSStyleRule !== "undefined" && rule instanceof CSSStyleRule) {
    return `${scopeSelectorText(rule.selectorText, scope)} { ${
      rule.style.cssText
    } }`;
  }

  // @media / @supports — scope what is inside, keep the condition itself.
  const atRule =
    typeof CSSMediaRule !== "undefined" && rule instanceof CSSMediaRule
      ? "@media"
      : typeof CSSSupportsRule !== "undefined" &&
        rule instanceof CSSSupportsRule
      ? "@supports"
      : null;

  if (atRule) {
    const inner = Array.from(rule.cssRules)
      .map((child) => scopeRule(child, scope))
      .join("\n");
    // Older engines expose the query as media.mediaText rather than
    // conditionText; without the fallback the whole block loses its condition.
    const condition = rule.conditionText || rule.media?.mediaText || "";
    return `${atRule} ${condition} {\n${inner}\n}`;
  }

  // @font-face, @keyframes, @page — no selectors that can reach the app.
  return rule.cssText;
};

const scopeCssText = (cssText, scope) => {
  if (!cssText || !cssText.trim()) return "";

  // `media="not all"` means the browser parses the rules but never applies
  // them, so reading them back cannot style anything on the way through.
  const styleEl = document.createElement("style");
  styleEl.media = "not all";
  styleEl.textContent = cssText;
  document.head.appendChild(styleEl);

  try {
    const sheet = styleEl.sheet;
    if (!sheet) {
      // Unreadable: drop the rules rather than let them loose on the app.
      console.warn("scopeDocumentCss: stylesheet could not be read; dropping it");
      return "";
    }
    return Array.from(sheet.cssRules)
      .map((rule) => scopeRule(rule, scope))
      .join("\n");
  } catch (err) {
    console.warn("scopeDocumentCss: stylesheet could not be scoped", err);
    return "";
  } finally {
    styleEl.remove();
  }
};

/**
 * Rewrites a sanitized document so its stylesheet only applies inside `scope`.
 *
 * @param {string} html   sanitized document markup
 * @param {string} scope  a selector for the container, e.g. '[data-doc-scope="3"]'
 * @returns {string}      markup with head styles inlined and every rule scoped
 */
export const scopeDocumentCss = (html, scope) => {
  if (!html || typeof html !== "string") return "";
  if (!scope) return html;

  const parsed = new DOMParser().parseFromString(html, "text/html");
  const styles = parsed.querySelectorAll("style");
  if (!styles.length) return html;

  styles.forEach((styleEl) => {
    styleEl.textContent = scopeCssText(styleEl.textContent, scope);
  });

  // <head> is dropped when the markup is injected, so its styles move inline.
  const headStyles = Array.from(parsed.head.querySelectorAll("style"))
    .map((styleEl) => styleEl.outerHTML)
    .join("");

  return `${headStyles}${parsed.body.innerHTML}`;
};

export default scopeDocumentCss;
